const LP_VID_COOKIE = "lp_vid";

export const getLpVid = () => {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${LP_VID_COOKIE}=([^;]*)`)
  );
  return match ? decodeURIComponent(match[1]) : "";
};

// EngageX answers `known: false` for a browser it has never linked to a lead.
// Page views from such a browser would only be dropped, so they are paused for
// the session - and resumed the moment this browser submits a form.
const PAGEVIEWS_OFF_KEY = "lp_ex_pv_off";

const pageViewsPaused = () => {
  try {
    return sessionStorage.getItem(PAGEVIEWS_OFF_KEY) === "1";
  } catch {
    return false;
  }
};

const setPageViewsPaused = (paused) => {
  try {
    if (paused) sessionStorage.setItem(PAGEVIEWS_OFF_KEY, "1");
    else sessionStorage.removeItem(PAGEVIEWS_OFF_KEY);
  } catch {}
};

// Posts one or more events to EngageX through our own API route, which adds
// the server-side token. Fire and forget; a failure never touches the page.
const sendEngageXEvents = (events) => {
  if (process.env.NEXT_PUBLIC_ENGAGEX_ENABLED !== "true") return;
  if (typeof window === "undefined" || !events.length) return;

  fetch("/api/engagex/event", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ lp_vid: getLpVid() || null, events }),
    keepalive: true,
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      if (data && data.known === false) setPageViewsPaused(true);
      if (data && data.known === true) setPageViewsPaused(false);
    })
    .catch(() => {});
};

// A link from one of our messages names its reader (signed, no contact data).
// Once EngageX accepts it, this browser is known, so page views resume.
// WhatsApp visit tag. The first 12 hex characters of the visitor id are
// placed inside the pre-typed WhatsApp message as invisible zero-width
// characters (ZWNJ = 0, ZWJ = 1). The customer sees only the normal message;
// EngageX decodes the tag when the chat reaches Wazzup, so the chat is linked
// to the pages and courses this browser viewed. No id is ever visible.
const TAG_ZERO = "‌";
const TAG_ONE = "‍";
const TAG_RE = /[‌‍]{8,}/;

export const whatsappVisitTag = () => {
  const hex = getLpVid().replace(/[^0-9a-f]/gi, "").slice(0, 12).toLowerCase();
  if (hex.length < 12) return "";
  return [...hex]
    .map((c) => parseInt(c, 16).toString(2).padStart(4, "0"))
    .join("")
    .replace(/0/g, TAG_ZERO)
    .replace(/1/g, TAG_ONE);
};

// Adds the invisible tag to a wa.me / api.whatsapp.com link's text once.
export const withWhatsAppVisitTag = (url) => {
  try {
    const tag = whatsappVisitTag();
    if (!tag || !url) return url;
    const [base, query = ""] = String(url).split("?");
    const params = new URLSearchParams(query);
    const text = params.get("text") || "Hello";
    if (TAG_RE.test(text)) return url;
    params.delete("text");
    const rest = params.toString();
    // Inside the message, after the first word: WhatsApp trims invisible
    // characters at the start and end of a message like it trims spaces.
    const cut = text.indexOf(" ");
    const tagged = cut > 0 ? `${text.slice(0, cut)}${tag}${text.slice(cut)}` : `${text}${tag}!`;
    return `${base}?${rest ? `${rest}&` : ""}text=${encodeURIComponent(tagged)}`;
  } catch {
    return url;
  }
};

export const identifyFromLink = (lx) => {
  if (process.env.NEXT_PUBLIC_ENGAGEX_ENABLED !== "true" || typeof window === "undefined" || !lx) return;
  fetch("/api/engagex/identify", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ lp_vid: getLpVid() || null, lx: String(lx).slice(0, 100) }),
    keepalive: true,
  })
    .then((res) => { if (res.ok) setPageViewsPaused(false); })
    .catch(() => {});
};

// One page view per client-side navigation. Path only: query strings can carry
// personal details (?email=...), and the journey rules only need the page.
export const trackPageView = (path) => {
  if (typeof window === "undefined" || !path || pageViewsPaused()) return;
  sendEngageXEvents([
    { name: "page_view", props: { path }, url: window.location.origin + path, at: new Date().toISOString() },
  ]);
};

export const notifyEngageXLead = ({ system, leadId, email, phone }) => {
  if (
    process.env.NEXT_PUBLIC_ENGAGEX_ENABLED !== "true" ||
    !leadId ||
    typeof window === "undefined"
  ) {
    return;
  }
  // This browser is a known lead from now on, so its page views matter again.
  setPageViewsPaused(false);

  fetch("/api/engagex/lead", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      lp_vid: getLpVid() || null,
      system,
      lead_id: String(leadId),
      email,
      phone,
    }),
    keepalive: true,
  }).catch((error) => {
    console.warn("EngageX lead link failed", error);
  });
};

// Adds the visitor's contact details to their Rybbit profile. Rybbit's Users
// list shows the `name` trait instead of the raw lp_vid, and searches `email`.
// Empty values are left out: Rybbit treats null as "delete this trait", and a
// single null makes the whole save fail.
export const identifyVisitor = ({ name, email, phone, company } = {}) => {
  if (typeof window === "undefined") return;
  const vid = getLpVid();
  if (!vid || typeof window.rybbit?.identify !== "function") return;

  const clean = (value) => (value == null ? "" : String(value).trim());
  const traits = Object.fromEntries(
    Object.entries({
      name: clean(name),
      // Rybbit's Users list shows `username` when it is set, so putting the
      // email there makes people identifiable at a glance instead of a UUID.
      username: clean(email).toLowerCase(),
      email: clean(email).toLowerCase(),
      phone: clean(phone),
      company: clean(company),
    }).filter(([, value]) => value)
  );
  if (!Object.keys(traits).length) return;

  try {
    window.rybbit.identify(vid, traits);
  } catch (error) {
    console.warn("Rybbit identify failed", error);
  }
};

// Sends a named business event to Rybbit. Properties describe the action, never
// the person: names, emails, phones and free-text messages are refused even if a
// caller passes them, because events are visible to everyone with dashboard
// access. Rybbit caps properties at 2,048 characters of JSON, so strings are
// trimmed.
const EVENT_PII_KEYS = new Set([
  "name", "email", "phone", "message",
  "first_name", "last_name", "firstName", "lastName",
]);

// ---------------------------------------------------------------------------
// Page context (Website Event Tracking Plan, section 9). Every event carries
// page_type and language; course pages also register their course so events
// fired from shared components (forms, buttons) know which course they are on.
// ---------------------------------------------------------------------------
const STATIC_PAGES = {
  "": "home", blog: "blog", testimonial: "testimonial", "corporate-trainings": "corporate",
  browsecourses: "listing", category_type: "category", about: "about", contact: "contact",
  "pay-now": "pay_now", lpTalks: "lp_talks", auth: "account", profile: "account",
};
let pageContext = {};

// On-site messages (EngageX, see EngageXOnsite.jsx) re-check their page rules
// and triggers on these window events. They stay inside the page; nothing is
// sent. Off until NEXT_PUBLIC_ENGAGEX_ONSITE_ENABLED is "true".
const ONSITE_ENABLED = process.env.NEXT_PUBLIC_ENGAGEX_ONSITE_ENABLED === "true";
const tellOnsite = (type, detail) => {
  if (!ONSITE_ENABLED || typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(type, { detail }));
  } catch {}
};

/** Course pages call this on mount: { page_type: "course" | "location", course_name, category, rating, enrolled_count }. */
export const setPageContext = (context = {}) => {
  pageContext = { ...context, path: typeof window !== "undefined" ? window.location.pathname : "" };
  tellOnsite("lp:context");
};

export const currentPageInfo = () => {
  if (typeof window === "undefined") return {};
  const parts = window.location.pathname.split("/").filter(Boolean);
  const language = parts[0] === "ar" || document.documentElement.lang === "ar" ? "ar" : "en";
  if (parts[0] === "ar" || parts[0] === "en") parts.shift();
  const first = parts[0] || "";
  // Context from a course page only counts while we are still on that page.
  const ctx = pageContext.path === window.location.pathname ? pageContext : {};
  let pageType = ctx.page_type || STATIC_PAGES[first];
  if (!pageType) pageType = first === "p" || parts.length === 1 ? (/-in-[a-z-]+$/.test(parts[parts.length - 1] || "") ? "location" : "course") : "other";
  return { page_type: pageType, language, course_name: ctx.course_name, category: ctx.category };
};

// The plan's names for the four business events we already send under older
// names (EngageX journeys and Insights depend on those). GTM gets the plan name.
const PLAN_NAMES = {
  enquiry_submitted: "generate_lead", demo_booked: "generate_lead", pay_now_submitted: "generate_lead",
  upskill_enquiry_submitted: "generate_lead", brochure_downloaded: "brochure_download",
  whatsapp_clicked: "whatsapp_click", course_clicked: "nav_course_click",
};

// The Rybbit script loads after the page becomes interactive, so events fired
// on first render (view_course, form_open of an inline form) used to be lost.
// Hold them until it is there (checked every 250 ms, up to 15 s), in order.
const rybbitQueue = [];
let rybbitWaiting = false;
const sendToRybbit = (name, props) => {
  const ready = () => typeof window.rybbit?.event === "function";
  const send = (n, p) => {
    try {
      window.rybbit.event(n, p);
    } catch (error) {
      console.warn("Rybbit event failed", error);
    }
  };
  if (ready() && !rybbitQueue.length) return send(name, props);
  if (rybbitQueue.length < 50) rybbitQueue.push([name, props]);
  if (rybbitWaiting) return;
  rybbitWaiting = true;
  let tries = 0;
  const timer = setInterval(() => {
    if (ready()) {
      clearInterval(timer);
      rybbitWaiting = false;
      rybbitQueue.splice(0).forEach(([n, p]) => send(n, p));
    } else if (++tries >= 60) {
      clearInterval(timer);
      rybbitWaiting = false;
      rybbitQueue.length = 0;
    }
  }, 250);
};

export const trackEvent = (name, props = {}) => {
  if (typeof window === "undefined" || !name) return;

  const page = currentPageInfo();
  const merged = {
    page_type: page.page_type,
    language: page.language,
    ...(page.course_name && props.course_name === undefined && props.course === undefined ? { course_name: page.course_name } : {}),
    ...props,
  };
  const clean = Object.fromEntries(
    Object.entries(merged)
      .filter(([key, value]) => !EVENT_PII_KEYS.has(key) && value != null && value !== "")
      .map(([key, value]) => [key, typeof value === "string" ? value.trim().slice(0, 120) : value])
  );
  // Personal keys are already gone; a copy, so a listener cannot change what is sent.
  tellOnsite("lp:track", { name, props: { ...clean } });

  // Rybbit keeps the analytics; EngageX uses the same event to start journeys.
  sendToRybbit(name, clean);
  // GTM / GA4 / Meta read the dataLayer, under the tracking plan's event names.
  try {
    window.dataLayer = window.dataLayer || [];
    // course_clicked is the mega-menu's nav_course_click; from a course card it is select_course.
    const planName = name === "course_clicked" && props.source !== "course_menu" ? "select_course" : PLAN_NAMES[name];
    window.dataLayer.push({ event: planName || name, ...clean, ...(planName ? { lp_event: name } : {}) });
  } catch {}
  // EngageX keeps events of known leads only. For an anonymous browser the
  // business events still go (as before: a form submit may be what makes the
  // browser known), but the new high-volume ones (scroll, time, clicks) do not.
  if (pageViewsPaused() && !PLAN_NAMES[name] && name !== "newsletter_subscribed") return;
  sendEngageXEvents([
    { name, props: clean, url: window.location.href, at: new Date().toISOString() },
  ]);
};
